import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Mail } from 'lucide-react';
import Logo from '../components/Logo';
import { forgotPassword } from '../services/api';

// Paso 1 del flujo "Olvidé mi contraseña". Pide el email del usuario; al
// enviar, el backend manda un email con un enlace a /reset-password con
// un token de un solo uso. El backend responde SIEMPRE 200 con un mensaje
// genérico (exista o no el email) para no permitir enumerar cuentas, así
// que el texto de éxito es neutro y solo mostramos error en fallos de
// red/servidor.
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Introduce tu email.'); return; }
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      if (err?.status === 400 && err?.message) {
        setError(err.message);
      } else {
        setError('No se pudo procesar la solicitud. Inténtalo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background:
          'radial-gradient(ellipse at top right, #E0F2FE 0%, transparent 50%), radial-gradient(ellipse at bottom left, #F1F5F9 0%, transparent 50%), #F8FAFC',
      }}
    >
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-ink-muted hover:text-primary-dark text-sm font-medium mb-8 transition-colors">
          <ArrowLeft size={16} /> Volver al login
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="shadow-lg shadow-primary/30 rounded-[10px]">
              <Logo size={48} variant="onLight" />
            </div>
            <span className="font-display font-bold text-2xl text-ink">TemControl</span>
          </div>
          <p className="text-sm text-ink-muted">Recuperar contraseña</p>
        </div>

        <div
          className="bg-white rounded-3xl p-8 md:p-10"
          style={{ boxShadow: '0 40px 80px -20px rgba(19, 78, 74, 0.15), 0 0 0 1px rgba(19, 78, 74, 0.04)' }}
        >
          {sent ? (
            <>
              <div className="flex items-center gap-2 text-emerald-700 mb-4">
                <CheckCircle2 size={20} />
                <h2 className="font-display text-xl font-bold">Revisa tu email</h2>
              </div>
              <p className="text-sm text-ink-muted mb-6">
                Si el email está registrado, recibirás un correo con un enlace para restablecer
                tu contraseña. Revisa también la carpeta de spam.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white py-3 rounded-xl font-semibold text-[0.95rem] hover:bg-primary-dark transition-all"
              >
                Volver al login <ArrowRight size={18} />
              </Link>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold mb-1">¿Olvidaste tu contraseña?</h2>
              <p className="text-sm text-ink-muted mb-6">
                Introduce el email asociado a tu cuenta y te enviaremos un enlace para restablecerla.
              </p>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">Email</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                      autoFocus
                      className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-[0.95rem] bg-slate-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 transition"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-xl font-semibold text-[0.95rem] flex items-center justify-center gap-2 hover:bg-primary-dark transition-all hover:shadow-lg hover:shadow-primary/30 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Enviar enlace <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-ink-muted text-xs mt-6">
          © {new Date().getFullYear()} TemControl · Gestión horaria
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
